import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Free Missionary Newsletter Template + 3 Real Examples Teardown';
const DESCRIPTION =
    'A free, copy-paste missionary newsletter template — no signup — plus a teardown of three common newsletter mistakes and the one fix that doubles engagement.';

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover13.jpg',
            datePublished: '2026-07-20',
            dateModified: '2026-07-20',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/free-missionary-newsletter-template',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How long should a missionary newsletter be?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "350–600 words. Long enough for one real scene, short enough that a tired reader finishes it on their phone. Anything longer belongs on your blog with a link from the email.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How often should I send a missionary newsletter?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Monthly is the standard cadence. It's frequent enough to stay in front of supporters and infrequent enough that each one still feels worth opening.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What should a missionary newsletter include?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'One line on the season, one specific story, what that story means for the bigger work, one prayer ask, a short practical update, and a closing line that sounds like you — the six-block template above.',
                    },
                },
            ],
        },
    ],
};

export default function FreeMissionaryNewsletterTemplate() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/free-missionary-newsletter-template' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-newsletter-template-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover13.jpg" alt="A group of people joining hands together" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>If you have ever opened a blank doc to write your monthly update and stared at it for forty-five minutes before closing it again, this post is for you. Below is a free newsletter template you can copy into Google Docs, Substack, or email. Then a teardown of three composite newsletters built from common patterns seen across missionary creators — what works, what falls flat, and the one fix in each that doubled donor engagement when the writer made it.</p>

                    <p>No signup required. Copy, paste, edit, send.</p>

                    <h2>The 6-block template (copy this)</h2>

                    <p>Every newsletter people actually read does roughly six things in roughly this order. The order matters more than the polish.</p>

                    <ol>
                        <li><strong>One sentence on the season.</strong> Where are you, what's the rhythm right now. One sentence. Not "many of you have asked how we are doing." Try: <em>"We're three weeks into the rainy season here in Chiang Mai and the language school just started its quarterly testing week."</em></li>
                        <li><strong>One specific story.</strong> A single scene — a conversation, a teaching moment, a problem you ran into, a small win. 150–250 words. The whole newsletter rises or falls on this paragraph.</li>
                        <li><strong>What the story is doing in the larger work.</strong> Two sentences. Why this scene matters for the long-term ministry. Not abstract; concrete. <em>"This is what discipling looks like when the person being discipled is older than you and your Thai is still bad."</em></li>
                        <li><strong>One ask.</strong> A prayer request, a need, or an invitation. One. Not three. The newsletters that ask for everything get nothing.</li>
                        <li><strong>A practical update.</strong> Travel, family, support level, kid milestones. The blocky logistical paragraph that proves you're a real person, not a charity. Three to five sentences.</li>
                        <li><strong>A closing line that reads like a friend wrote it.</strong> Not "yours in His service." Try: <em>"Thanks for reading. If you've got a minute this week, pray for Naa — she's the one in the story above."</em></li>
                    </ol>

                    <p>The whole thing should run 350–600 words. Anything longer is a blog post and belongs on your site, with a link from the email.</p>

                    <h2>Three examples, torn apart</h2>

                    <p>These are composites — pulled from patterns repeated across hundreds of missionary newsletters, with details changed. This isn't a critique of any specific person.</p>

                    <h3>Example 1 — The "Update From The Field" newsletter</h3>

                    <blockquote>
                        <p><em>Dear friends and family,</em></p>
                        <p><em>Many of you have been asking how things are going here in [country]. We have been so blessed to see God moving in this place. This month has been busy with ministry, school for the kids, language study, and many other things. We are so grateful for your prayers and support…</em></p>
                    </blockquote>

                    <p>Three things this newsletter does wrong:</p>

                    <ul>
                        <li><strong>Opens with the wrong sentence.</strong> "Dear friends and family" plus "many of you have been asking" is the universal missionary-newsletter signature for "what follows will not contain anything specific." Readers learn it within a year and start skimming the first paragraph and skipping to the photos.</li>
                        <li><strong>No scene.</strong> The writer tells you they had a busy month. They don't show you a single moment of that month.</li>
                        <li><strong>Stacked asks at the bottom.</strong> Pray for the work, pray for the kids, pray for the team, pray for our budget. Each of these is real. But four asks in one paragraph means none of them land.</li>
                    </ul>

                    <p><strong>The fix that doubles engagement:</strong> Replace the entire opening paragraph with one sentence about a specific moment from this week. The story-first newsletter is the single highest-leverage change a missionary writer can make. Donors who reply do it after stories, not after summaries.</p>

                    <h3>Example 2 — The "Look at All These Photos" newsletter</h3>

                    <p>A six-paragraph newsletter, twelve photos, one caption per photo. The photos are great. The team smiling at language school. The kids on the bus. A market scene. A prayer meeting.</p>

                    <p>What's missing: a single sentence that connects one of those scenes to the long ministry arc. Photos do the work of <em>presence</em> but they do not do the work of <em>meaning</em>. Donors looking at twelve photos with thin captions feel like they're flipping through someone else's vacation. Donors looking at three photos plus one paragraph that says <em>"This is the third Tuesday we've met in this room. The man on the left came back because last week he asked a question and we didn't have time to answer it. This week we did."</em> feel like they were almost there.</p>

                    <p><strong>The fix that doubles engagement:</strong> Halve the photos. Triple the words per photo that remains. Photos serve the writing; the writing does not serve the photos.</p>

                    <h3>Example 3 — The "Newsletter As Report" newsletter</h3>

                    <p>This one is the trap that careful, conscientious missionaries fall into most often. The writer feels obligated to <em>account for the support</em>. So the newsletter becomes a kind of monthly performance review. Hours taught. Sermons preached. People reached. Budget delta. New supporters. Departing supporters.</p>

                    <p>It is admirable and it is unread.</p>

                    <p>The reason it's unread isn't that supporters don't care about outcomes. It's that you're treating them like a board reviewing a quarterly. The thing they actually wanted when they signed up to support you was <em>to be near someone whose life looks like yours doesn't.</em> That isn't a quarterly review.</p>

                    <p><strong>The fix that doubles engagement:</strong> Keep the report — but move it to the bottom and call it that. Put the story first. Let the report serve the story instead of being the newsletter.</p>

                    <h2>A small confession about format</h2>

                    <p>Whatever email tool or platform you use — Mailchimp, Substack, your agency's portal, Devotion Fund's update feature, even a plain bcc list in Gmail — the format matters less than the writing. There is no email platform that will save a newsletter with no scene in it. There is no email platform that will ruin a newsletter that opens with <em>"I had to tell a Thai grandmother last week that my mother also died of cancer, and we both cried, and now she lets me sit in her living room every Wednesday."</em></p>

                    <p>The writing is the thing. The tools just need to get out of its way.</p>

                    <h2>What every monthly newsletter needs (checklist)</h2>

                    <ul>
                        <li>One sentence on the season.</li>
                        <li>One specific scene (150–250 words).</li>
                        <li>Two sentences on what the scene is doing in the bigger picture.</li>
                        <li>One ask. One.</li>
                        <li>A blocky practical paragraph: travel, family, support, kids.</li>
                        <li>A closing line that sounds like you wrote it to a friend.</li>
                        <li>One link, maximum, if at all.</li>
                        <li>A clear way to support you — but not buried, not pushed.</li>
                    </ul>

                    <h2>The template, ready to copy</h2>

                    <p>Paste this into Google Docs, Substack, or your email client. Replace the bracketed prompts with your content.</p>

                    <pre>{`Subject: [One specific noun from the story below — e.g., "The third Tuesday in Naa's living room"]

[ONE-SENTENCE SEASON OPENER — what the rhythm of life is right now]

[ONE SCENE — 150–250 words. A conversation, a moment, a small win, a problem.
Write it like you're telling a friend at coffee. Use names. Use dialogue if there was any.]

[TWO SENTENCES — what this scene says about the bigger work. Not abstract.]

One thing to pray with us about this month:
[Single ask. Specific. Pray-able. "Pray for Naa, who…" is better than "Pray for our ministry."]

The blocky update part:
[Three to five sentences on travel, family, language study, kid milestones, anything
practical. Donors want to know you're a real person.]

[CLOSING LINE — sounds like you. Not "yours in His service." Try:
"Thanks for reading. If you have a minute, pray for [name from the story]."]

— [Your name and your spouse's, if applicable]

P.S. — [Optional. If your support level is moving, or you have a real partnership ask,
or there's something practical — put it here, plainly. Not buried.]`}</pre>

                    <h2>Frequently asked questions</h2>

                    <p><strong>How long should a missionary newsletter be?</strong><br />
                    350–600 words. Long enough for one real scene, short enough that a tired reader finishes it on their phone. Anything longer belongs on your blog with a link from the email.</p>

                    <p><strong>How often should I send a missionary newsletter?</strong><br />
                    Monthly is the standard cadence. It's frequent enough to stay in front of supporters and infrequent enough that each one still feels worth opening.</p>

                    <p><strong>What should a missionary newsletter include?</strong><br />
                    One line on the season, one specific story, what that story means for the bigger work, one prayer ask, a short practical update, and a closing line that sounds like you — the six-block template above.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-write-a-prayer-letter-people-actually-read">How to Write a Prayer Letter People Actually Read</a></li>
                        <li><a href="/resources/gofundme-alternatives-for-missionaries">GoFundMe Alternatives for Missionaries: 7 Platforms Compared (2026)</a> — covers where your newsletter sits in the broader fundraising stack.</li>
                        <li><a href="/resources/how-to-start-monthly-giving-page-without-501c3">How to Start a Monthly Giving Page as a Missionary (Without Becoming a 501(c)(3))</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>, a recurring-giving platform built for support-raised missionaries. We read a lot of missionary newsletters as part of the day job and built this template after noticing the same six-block pattern in the ones that actually work.</p>
                    <p><small><em>Last updated: 2026-07-20.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/free-missionary-newsletter-template" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
